import './TwoFactorAuth.css';
import {useState, useContext, useLayoutEffect, useRef, useEffect} from "react";
import { LocaleContext, LoginContext, OverlayContext, StateContext } from './App';
import { LoadState, TwoFactorAuthType } from './enums.mjs';
import { Navigate } from 'react-router-dom';
import { ErrorComp, LoadButton, NavigateSmart } from './Common';
import { postWithCsrf } from './util';
import { LogContext } from './LogDisplay';
import { useAutoLoad } from './hooks';

export function TwoFactorAuth(props) {
    const codeLength = 6;
    // const [code, setCode] = useState(Array(6));
    const state = useContext(StateContext);
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const overlay = useContext(OverlayContext);
    const log = useContext(LogContext);

    const [readOnly, setReadOnly] = useState(true);
    const [curCellId, setCurCellId] = useState(0);
    const [firstEffect, setFirstEffect] = useState(true);

    const [nextSmsCountS, setNextSmsCountS] = useState(props.nextSmsCountS);
    const [resendState, setResendState] = useState(LoadState.NONE);

    const resendSmsTimeoutRef = useRef(null);
    const initRef = useRef(true);

    const [loadState, setLoadState] = useState(LoadState.LOADING);


    // FUNCTIONS:

    function checkCode() {
        let code = "";

        for(let i = 0; i < codeLength; i++) {
            const ele = document.getElementById("code_input_cell_" + i);
            
            if(ele.value == "") {
                return;
            }

            code += ele.value;
        }

        props.on2faCodeEntered(code);
    }

    function handleKeyDown(e) {
        setResendState(LoadState.NONE);
        
        if(e.key == "Backspace") {
            e.preventDefault();

            const id = e.target.id;
            const idNum = id.charAt(id.length - 1);
            const idNumNum = Number(idNum) - 1;

            if(e.target.value == "" && idNum > 0 && idNum < codeLength) {
                setCurCellId(idNumNum);
                let ele = document.getElementById("code_input_cell_" + idNumNum);
                ele.value = "";
            }
            else {
                e.target.value = "";
            }
        }
        else if(e.key.match(/^[0-9]$/)){
            e.preventDefault();
            e.target.value = e.key;

            const id = e.target.id;
            const idNum = id.charAt(id.length - 1);
            const idNumNum = Number(idNum) + 1;

            if(idNum >= 0 && idNum < codeLength - 1) {
                setCurCellId(idNumNum);
            }

            checkCode();
        }

        props.on2faCodeChange();
    }

    function handlePaste(e) {
        e.preventDefault();

        setResendState(LoadState.NONE);

        const paste = e.clipboardData.getData("text");

        handlePastedStr(paste);
    }

    function handlePastedStr(str) {
        // Allow only numbers to be pasted
        const pasteFiltered = str.replace(/[^0-9]/g, "");

        let len = codeLength;

        if(pasteFiltered.length < len) {
            len = pasteFiltered.length;
        }

        for(let i = 0; i < len; i++) {
            const ele = setNumToCell(pasteFiltered[i], i);

            if(i == len - 1){
                ele.focus();
                setCurCellId(i);
            }
        }

        for(let i = len; i < codeLength; i++) {
            setNumToCell("", i);
        }

        checkCode();
        props.on2faCodeChange();
    }

    function setNumToCell(num, i) {
        const ele = document.getElementById("code_input_cell_" + i);
        ele.value = num;
        return ele;
    }

    function handleInput(e) {
        setResendState(LoadState.NONE);

        const v = e.target.value;

        if(v.match(/^\d+$/)) {
            // Safari IOS inputs one digit after the other when using the "from sms" functionality...
            if(v.length === codeLength) {
                handlePastedStr(v);
            }
        }
        else {
            e.target.value = "";
        }
    }
    
    function resendSms() {
        try {
            let data = "";

            if(props.username && props.password) {
                data = JSON.stringify({email: props.username, password: props.password});
            }

            let noErrState = false;

            postWithCsrf("/user/resend_two_factor_auth_mobile", data, state).then(response => {
                if(response.ok) {
                    return response.json();
                }
                else {
                    if(response.status == 522) {
                        overlay.setErrorPopupInfo({"title": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LIMIT_ERROR,
                                "text": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LOCKED_CODE_STILL_VALID});  // Do not use the activation variant here
                        overlay.setErrorPopupDisplayed(true);

                        noErrState = true;
                    }
                    else if(response.status == 523) {
                        return response.text().then((text) => {
                            overlay.setErrorPopupInfo({"title": locale.msgs.TWO_FACTOR_AUTH_MOBILE_LIMIT_ERROR,
                                    "text": props.activation ? locale.msgs.TWO_FACTOR_AUTH_MOBILE_LOCKED_CODE_EXPIRED_ACTIVATION :
                                                                locale.msgs.TWO_FACTOR_AUTH_MOBILE_LOCKED_CODE_EXPIRED.replace("{{hours}}", text)});
                            overlay.setErrorPopupDisplayed(true);

                            noErrState = true;
                        });
                    }

                    throw new Error("Response code: " + response.status);
                }
            }).then(json => {
                if(!json.next_sms_count_s) {
                    throw new Error("next_sms_count_s missing");
                }

                setNextResendTimeout(json.next_sms_count_s);

                setResendState(LoadState.LOAD_SUCCESS);
            }).catch(error => {
                if(noErrState) {
                    setResendState(LoadState.NONE);
                }
                else {
                    setResendState(LoadState.LOAD_ERROR);
                }
                
                console.log(error);
            });

            // Calling setLoadState() after postWithCsrf() as it could throw an exception
            setResendState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function setNextResendTimeout(next_s) {
        if(resendSmsTimeoutRef.current) {
            clearTimeout(resendSmsTimeoutRef.current);
        }

        setNextSmsCountS(Math.round(next_s));

        resendSmsTimeoutRef.current = setTimeout(() => {
            setNextResendTimeout(next_s - 1);
        }, 1000);
    }
    

    // EFFECTS:

    useLayoutEffect(() => {
        function handleBlur(e) {
            e.target.focus();
        }

        const ele = document.getElementById("code_input_cell_" + curCellId);

        if(ele) {
            if(window.innerWidth >= 450) {
                ele.focus();
            }
            else {
                // if(firstEffect) {
                //     setFirstEffect(false);
                // }
                // else {
                    ele.focus();
                // }
            }

            ele.addEventListener("blur", handleBlur);
            return () => ele.removeEventListener("blur", handleBlur);
        }
        
    }, [curCellId, loadState]);

    useEffect(() => {
        // Ensure that this only happens initially
        if(initRef.current) {
            if(props.nextSmsCountS) {
                setNextResendTimeout(props.nextSmsCountS);
            }

            initRef.current = false;
        }
    }, []);

    useEffect(() => {
        if(props.twoFactorType && props.twoFactorType !== TwoFactorAuthType.MOBILE) {
            setLoadState(LoadState.LOAD_SUCCESS);
            return;
        }

        try {
            postWithCsrf("/user/get_two_factor_next_sms", JSON.stringify({email: props.username, password: props.password}), state).then(response => {
                if(response.ok) {
                    return response.json();
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).then(json => {
                setNextResendTimeout(Math.round(json.next_sms_count_s));
                setLoadState(LoadState.LOAD_SUCCESS);
            }).catch(error => {
                console.log(error);
                setLoadState(LoadState.LOAD_ERROR);
            }).finally(() => {
                overlay.decrLoadingCounter();
            });

            // Calling setAcceptSaveState() after postWithCsrf() as it could throw an exception
            setLoadState(LoadState.LOADING);
            overlay.incrLoadingCounter();
        }
        catch(e) {
            console.log(e);
        }
    }, []);


    // RENDER:

    let cells = [];

    for(let i = 0; i < 6; i++) {
        let name = "code_input_cell_" + i;

        cells.push(<input id={name} name={name} key={name} onKeyDown={handleKeyDown} onPaste={handlePaste}
                        onInput={handleInput} type="text"
                        maxLength="6" inputMode="numeric" pattern="[0-9]*" className="code_input_cell"
                        autoComplete={ i === 0 ? "one-time-code" : null}></input>);
    }

    if(loadState === LoadState.LOAD_ERROR) {
        return <ErrorComp text={locale.msgs.GENERAL_ERROR_MANAGEMENT_DESCR} />;
    }
    else if(loadState !== LoadState.LOAD_SUCCESS) {
        return null;
    }

    if(!props.twoFactorType || props.twoFactorType === TwoFactorAuthType.NONE) {
        return <NavigateSmart to={props.management ? "/login" : "/login/"} />
    }

    return (
        <div id="two_factor_auth">
            { props.twoFactorType === TwoFactorAuthType.APP ?
                [<div className="head">{locale.msgs.ENTER_TWO_FACTOR_CODE_APP}</div>,
                <div className="text">{locale.msgs.ENTER_TWO_FACTOR_CODE_APP_DESCR}</div>]
            :
                [<div className="head">{locale.msgs.ENTER_TWO_FACTOR_CODE_SMS}</div>,
                <div className="text">{locale.msgs.ENTER_TWO_FACTOR_CODE_SMS_DESCR}</div>]
            }

            <div className="text_big" id="code_label">{locale.msgs.SIX_DIGIT_CODE}</div>
            <div className="code_input_box">
                {cells}
            </div>

            { props.twoFactorType === TwoFactorAuthType.MOBILE ?
                [<div className="resend_two_fac_sms_box">
                    { nextSmsCountS <= 0 ?
                        [<div className="loader" style={{visibility: "hidden"}}></div>,
                        <div className="text_small_small_2 resend_two_fac_sms" onClick={resendSms}>{locale.msgs.RESEND_SMS}</div>,
                        <div className="loader" style={{visibility: resendState === LoadState.LOADING  ? "visible" : "hidden"}}></div>]
                    :
                        <div className="text_small_small_2">{locale.msgs.RESEND_SMS_WAIT.replace("{{seconds}}", nextSmsCountS)}</div>
                    }
                    
                </div>,
                resendState === LoadState.LOAD_ERROR ? <div className="text_error_3 two_fac_resend_sms_error">Fehler</div> : null]

            :
                null
            }

        </div>
    );
}
